import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Check, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { fmtINR, fmtDate } from "@/lib/leadUtils";
import { useIsMobile } from "@/hooks/useIsMobile";
import VoidTransactionModal from "@/components/accounts/VoidTransactionModal";
import ProjectExpensesSection from "@/components/accounts/ProjectExpensesSection";
import ProjectPnlSection from "@/components/accounts/ProjectPnlSection";

const TITLE_LABEL = { advance: "Advance", mid: "Mid", balance: "Balance", extra: "Extra" };

export default function ProjectPaymentSlideOver({ projectId, onClose, onChanged, onRecord, onOpenProject }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/accounts/projects/${projectId}/payment-detail`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load payment details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [projectId]);

  const onVoided = () => { setVoidTarget(null); refresh(); onChanged && onChanged(); };

  if (loading || !data) {
    return (
      <div className="fixed inset-0" data-testid="slide-over-loading" style={{ zIndex: 10060 }}>
        <div className="absolute inset-0" style={{ background: "rgba(34,56,44,0.4)" }} onClick={onClose} />
        <aside style={{
          position: "absolute",
          right: 0, top: 0, bottom: 0,
          left: isMobile ? 0 : "auto",
          width: isMobile ? "100vw" : "min(440px, 95vw)",
          background: "#FDFAF4",
          borderLeft: isMobile ? "none" : "1px solid #E4DCCD",
          boxShadow: isMobile ? "none" : "-8px 0 32px rgba(60,46,20,.1)",
          padding: 24,
        }}>
          <div className="kicker">Loading</div>
        </aside>
      </div>
    );
  }

  const s = data.payment_summary || {};
  const nd = s.next_due_installment;
  const displayName = data.client_name + (data.partner_name ? ` × ${data.partner_name}` : "");
  const schedule = s.schedule || [];
  const paidByLabel = s.paid_by_label || {};

  return (
    <div className="fixed inset-0" data-testid="payment-slide-over" style={{ zIndex: 10060 }}>
      <div className="absolute inset-0" style={{ background: "rgba(34,56,44,0.4)" }} onClick={onClose} />
      <aside style={{
        position: "absolute",
        right: 0, top: 0, bottom: 0,
        left: isMobile ? 0 : "auto",
        width: isMobile ? "100vw" : "min(440px, 95vw)",
        background: "#FDFAF4",
        borderLeft: isMobile ? "none" : "1px solid #E4DCCD",
        boxShadow: isMobile ? "none" : "-8px 0 32px rgba(60,46,20,.1)",
        overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{
          position: "sticky",
          top: 0,
          background: "#FDFAF4",
          borderBottom: "1px solid #E4DCCD",
          padding: "20px 24px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          zIndex: 5,
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#B08D55",
              marginBottom: 6,
            }}>
              PAYMENT DETAIL
            </div>
            <h3
              className="font-display"
              style={{ fontSize: 26, lineHeight: 1.15, color: "#22382C", wordBreak: "break-word" }}
              data-testid="slide-over-title"
            >
              {displayName}
            </h3>
            <div style={{ fontSize: 12, color: "#76705F", marginTop: 8 }}>
              {data.wedding_date && <>Wedding · {fmtDate(data.wedding_date)} · </>}
              {data.status}
            </div>
            <button
              onClick={() => onOpenProject && onOpenProject(data.id)}
              data-testid="slide-over-open-project"
              style={{
                marginTop: 8,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#B08D55",
                transition: "color .15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#8E7044")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#B08D55")}
            >
              Open project →
            </button>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="slide-over-close"
            style={{
              padding: 6,
              color: "#A39A8B",
              borderRadius: 6,
              transition: "color .15s, background-color .15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#22382C"; e.currentTarget.style.background = "#F4EDDF"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#A39A8B"; e.currentTarget.style.background = "transparent"; }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 26 }}>
          {/* 2×2 Summary grid */}
          <div
            data-testid="slide-over-summary"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <Mini label="Quote" value={fmtINR(data.total_quote_value)} />
            <Mini label="Collected" value={fmtINR(s.total_collected)} color="#3F6B51" />
            <Mini
              label="Outstanding"
              value={fmtINR(s.total_outstanding)}
              color={(s.total_outstanding || 0) > 0 ? "#A35C45" : "#22382C"}
            />
            <Mini
              label="Next due"
              value={nd ? `${TITLE_LABEL[nd.label] || nd.label}` : "—"}
              hint={nd?.expected_due_date ? fmtDate(nd.expected_due_date) : (s.total_outstanding === 0 ? "All clear ✓" : "")}
              color={s.is_overdue ? "#A35C45" : "#22382C"}
            />
          </div>

          {/* Payment schedule */}
          <div>
            <SectionLabel>PAYMENT SCHEDULE</SectionLabel>
            {schedule.length === 0 ? (
              <p style={{ fontSize: 13, fontStyle: "italic", color: "#76705F" }}>No schedule captured by wizard.</p>
            ) : (
              <div data-testid="slide-over-schedule" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {schedule.map((inst) => {
                  const paid = paidByLabel[inst.label] || 0;
                  const done = paid >= inst.expected_amount;
                  return (
                    <div
                      key={inst.label}
                      data-testid={`slide-over-schedule-${inst.label}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        background: "#FFFFFF",
                        border: "1px solid #EDE5D8",
                        borderRadius: 9,
                        padding: "8px 10px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                        {done ? (
                          <Check size={14} strokeWidth={2.5} style={{ color: "#3F6B51", flexShrink: 0 }} />
                        ) : (
                          <Clock size={14} strokeWidth={2} style={{ color: "#B08D55", flexShrink: 0 }} />
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#22382C", lineHeight: 1.25 }}>
                            {TITLE_LABEL[inst.label] || inst.label}
                          </div>
                          <div style={{ fontSize: 11, color: "#76705F", marginTop: 1 }}>
                            {fmtINR(inst.expected_amount)}
                            {inst.due_date ? ` · Due ${fmtDate(inst.due_date)}` : ""}
                          </div>
                        </div>
                      </div>
                      <Chip done={done} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Transactions */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 8 }}>
              <SectionLabel style={{ marginBottom: 0 }}>TRANSACTIONS</SectionLabel>
              {(s.total_outstanding || 0) > 0 && (
                <button
                  onClick={() => onRecord && onRecord(data)}
                  data-testid="slide-over-record-payment"
                  style={{
                    background: "#22382C",
                    color: "#F4EFE4",
                    border: "none",
                    borderRadius: 6,
                    padding: "8px 14px",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    transition: "background-color .15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#152823")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#22382C")}
                >
                  + Record payment
                </button>
              )}
            </div>
            {(data.transactions || []).length === 0 ? (
              <p style={{ fontSize: 13, fontStyle: "italic", color: "#76705F" }}>No transactions yet.</p>
            ) : (
              <div data-testid="slide-over-transactions" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {data.transactions.map((t) => (
                  <div
                    key={t.transaction_id}
                    data-testid={`slide-over-txn-${t.transaction_id}`}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 10,
                      background: "#FFFFFF",
                      border: "1px solid #EDE5D8",
                      borderRadius: 9,
                      padding: "8px 10px",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontSize: 13,
                        fontWeight: 600,
                        textDecoration: t.voided ? "line-through" : "none",
                        color: t.voided ? "#A39A8B" : "#22382C",
                      }}>
                        {TITLE_LABEL[t.installment_label] || t.installment_label} · {t.method}
                        {t.reference && <span style={{ fontWeight: 400, fontSize: 11, color: "#76705F" }}> · {t.reference}</span>}
                        {t.voided && (
                          <span style={{
                            marginLeft: 6,
                            padding: "2px 6px",
                            fontSize: 9,
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                            background: "#FBEAE3",
                            color: "#A35C45",
                            borderRadius: 4,
                            fontWeight: 700,
                          }}>
                            VOIDED
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "#76705F", marginTop: 2 }}>
                        {fmtDate(t.received_on)}
                        {t.notes && <> · {t.notes}</>}
                        {t.voided && t.void_reason && (
                          <> · Reason: {t.void_reason}</>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{
                        fontSize: 13,
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        textDecoration: t.voided ? "line-through" : "none",
                        color: t.voided ? "#A39A8B" : "#3F6B51",
                      }}>
                        {fmtINR(t.amount)}
                      </div>
                      {!t.voided && (
                        <button
                          onClick={() => setVoidTarget(t)}
                          data-testid={`slide-over-void-${t.transaction_id}`}
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: "#B08D55",
                            marginTop: 4,
                            minHeight: 24,
                            padding: "4px 2px",
                            transition: "color .15s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = "#8E7044")}
                          onMouseLeave={(e) => (e.currentTarget.style.color = "#B08D55")}
                        >
                          Void
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Project expenses (Slice B) */}
          <ProjectExpensesSection projectId={data.id} onChanged={onChanged} />

          {/* P&L rollup (Slice C) */}
          <ProjectPnlSection projectId={data.id} />
        </div>
      </aside>

      {voidTarget && (
        <VoidTransactionModal
          transaction={voidTarget}
          onClose={() => setVoidTarget(null)}
          onVoided={onVoided}
        />
      )}
    </div>
  );
}

function SectionLabel({ children, style }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: "#B08D55",
        marginBottom: 12,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Chip({ done }) {
  return (
    <span
      style={{
        background: done ? "#E4EDDF" : "#F4EDDF",
        color: done ? "#3F6B51" : "#9A7A3C",
        fontWeight: 700,
        fontSize: 9,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        padding: "3px 7px",
        borderRadius: 6,
        display: "inline-block",
        flexShrink: 0,
      }}
    >
      {done ? "Paid" : "Pending"}
    </span>
  );
}

function Mini({ label, value, hint, color }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E9E2D6",
        borderRadius: 10,
        padding: 18,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.13em",
          textTransform: "uppercase",
          color: "#A39A8B",
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        className="font-display"
        style={{ fontSize: 22, lineHeight: 1.15, color: color || "#22382C", wordBreak: "break-word" }}
      >
        {value}
      </div>
      {hint && <div style={{ fontSize: 11, color: "#76705F", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
